import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import {
  DEFAULT_WALL_HEIGHT,
  getCeilingHeightForFloor,
} from "../geometry";
import type { Floor3DShapeDefinition } from "../geometry";
import type { RoomType } from "../../../types/floorplannerTypes";

const ROOM_LIGHT_HEIGHT_OFFSET = 0.04;
const ROOM_LIGHT_FILL_DROP = 0.62;
const DAYLIGHT_INTENSITY_SCALE = 1.18;
const DAYLIGHT_SHADOW_MAP_SIZE = 2048;
const DAYLIGHT_SHADOW_RADIUS = 1.5;
const DAYLIGHT_SHADOW_BLUR_SAMPLES = 4;
const DEFAULT_AMBIENT_INTENSITY = 0.42;
const DEFAULT_SKY_INTENSITY = 0.54;

let rectAreaLightUniformsReady = false;
let rectAreaLightUniformsPromise: Promise<void> | null = null;

export type DaylightSettings = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  intensity: number;
  ambientIntensity: number;
  skyIntensity: number;
  color: THREE.Color;
  shadowExtent: number;
};

export type RoomCeilingLightInfo = {
  id: string;
  position: THREE.Vector3;
  intensity: number;
  fillIntensity: number;
  distance: number;
  diffuserSize: THREE.Vector2;
};

export const SunLight: React.FC<{
  settings: DaylightSettings;
  castShadow?: boolean;
}> = ({ settings, castShadow = true }) => {
  const target = useMemo(() => new THREE.Object3D(), []);

  target.position.copy(settings.target);
  target.updateMatrixWorld();

  useLayoutEffect(() => {
    target.position.copy(settings.target);
    target.updateMatrixWorld(true);
  }, [settings.target, target]);

  return (
    <>
      <primitive object={target} />
      <directionalLight
        castShadow={castShadow}
        color={settings.color}
        intensity={settings.intensity}
        position={[
          settings.position.x,
          settings.position.y,
          settings.position.z,
        ]}
        target={target}
        shadow-mapSize-width={DAYLIGHT_SHADOW_MAP_SIZE}
        shadow-mapSize-height={DAYLIGHT_SHADOW_MAP_SIZE}
        shadow-camera-left={-settings.shadowExtent}
        shadow-camera-right={settings.shadowExtent}
        shadow-camera-top={settings.shadowExtent}
        shadow-camera-bottom={-settings.shadowExtent}
        shadow-camera-near={0.1}
        shadow-camera-far={settings.shadowExtent * 4}
        shadow-bias={-0.00025}
        shadow-normalBias={0.035}
        shadow-radius={DAYLIGHT_SHADOW_RADIUS}
        shadow-blurSamples={DAYLIGHT_SHADOW_BLUR_SAMPLES}
      />
    </>
  );
};

export const RoomCeilingLights: React.FC<{
  lights: RoomCeilingLightInfo[];
}> = ({ lights }) => (
  <>
    {lights.map((light) => (
      <SoftRoomCeilingLight key={light.id} light={light} />
    ))}
  </>
);

const SoftRoomCeilingLight: React.FC<{ light: RoomCeilingLightInfo }> = ({
  light,
}) => {
  const areaLightRef = useRef<THREE.RectAreaLight>(null);
  const [areaLightReady, setAreaLightReady] = useState(
    rectAreaLightUniformsReady,
  );

  useEffect(() => {
    let mounted = true;
    loadRectAreaLightUniforms().then(() => {
      if (mounted) setAreaLightReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useLayoutEffect(() => {
    const areaLight = areaLightRef.current;
    if (!areaLight) return;

    areaLight.lookAt(light.position.x, light.position.y, 0);
    areaLight.updateMatrixWorld(true);
  }, [light.position.x, light.position.y, light.position.z]);

  return (
    <>
      {areaLightReady && (
        <rectAreaLight
          ref={areaLightRef}
          position={[light.position.x, light.position.y, light.position.z]}
          color={0xfff0cc}
          intensity={light.intensity}
          width={light.diffuserSize.x}
          height={light.diffuserSize.y}
        />
      )}
      <pointLight
        position={[
          light.position.x,
          light.position.y,
          Math.max(light.position.z - ROOM_LIGHT_FILL_DROP, 1.45),
        ]}
        color={0xfff1d2}
        intensity={light.fillIntensity}
        distance={light.distance}
        decay={2.4}
      />
    </>
  );
};

function loadRectAreaLightUniforms(): Promise<void> {
  if (rectAreaLightUniformsReady) return Promise.resolve();

  rectAreaLightUniformsPromise ??= import(
    "three/examples/jsm/lights/RectAreaLightUniformsLib.js"
  ).then(({ RectAreaLightUniformsLib }) => {
    RectAreaLightUniformsLib.init();
    rectAreaLightUniformsReady = true;
  });

  return rectAreaLightUniformsPromise;
}

export function getDaylightSettings(
  bounds: THREE.Box2,
  enabled: boolean,
  timeOfDay: number,
): DaylightSettings {
  const center2 = bounds.isEmpty()
    ? new THREE.Vector2(0, 0)
    : bounds.getCenter(new THREE.Vector2());
  const size = bounds.isEmpty()
    ? new THREE.Vector2(8, 8)
    : bounds.getSize(new THREE.Vector2());
  const maxDimension = Math.max(size.x, size.y, 4);
  const normalizedTime = THREE.MathUtils.clamp(timeOfDay, 0, 1);
  const daylightCurve = Math.max(0, Math.sin(normalizedTime * Math.PI));
  const azimuth = THREE.MathUtils.lerp(
    THREE.MathUtils.degToRad(-138),
    THREE.MathUtils.degToRad(42),
    normalizedTime,
  );
  const horizontalDistance = maxDimension * 1.35 + 5;
  const elevation = Math.max(daylightCurve, 0.04);
  const position = new THREE.Vector3(
    center2.x + Math.cos(azimuth) * horizontalDistance,
    center2.y + Math.sin(azimuth) * horizontalDistance,
    0.45 + elevation * (maxDimension * 1.15 + 3.5),
  );
  const warm = new THREE.Color(0xffc98a);
  const neutral = new THREE.Color(0xfff7e6);
  const color = warm.clone().lerp(neutral, daylightCurve);
  const directIntensity = enabled
    ? Math.pow(daylightCurve, 0.82) * DAYLIGHT_INTENSITY_SCALE
    : 0;

  return {
    position,
    target: new THREE.Vector3(center2.x, center2.y, 0.55),
    intensity: directIntensity,
    ambientIntensity: enabled
      ? 0.34 + daylightCurve * 0.18
      : DEFAULT_AMBIENT_INTENSITY,
    skyIntensity: enabled ? 0.42 + daylightCurve * 0.2 : DEFAULT_SKY_INTENSITY,
    color,
    shadowExtent: Math.max(maxDimension * 1.15, 7),
  };
}

/**
 * Soft ceiling lights use RectAreaLight + pointLight per floor. Dense
 * image-traced plans can have 100+ area fills; evaluating that many area lights
 * in the fragment shader dominates 3D frame time. Cap to the largest rooms —
 * sun + ambient already light the rest.
 */
export const MAX_ROOM_CEILING_LIGHTS = 12;

export function getRoomCeilingLights(
  floors: Floor3DShapeDefinition[],
  rooms: RoomType[],
  wallHeightById: Map<string, number>,
  bounds: THREE.Box2,
): RoomCeilingLightInfo[] {
  const candidates = floors
    .map((floor) => {
      const center = getShapeCenter(floor.shape);
      const shapeBounds = getShapeBounds(floor.shape);
      const size = shapeBounds.isEmpty()
        ? getFallbackBoundsSize(bounds)
        : shapeBounds.getSize(new THREE.Vector2());
      const area = Math.max(size.x, 0) * Math.max(size.y, 0);
      const height = getCeilingHeightForFloor(
        floor,
        rooms,
        wallHeightById,
        DEFAULT_WALL_HEIGHT,
      );
      const maxSize = Math.max(size.x, size.y, 3);
      const diffuserSize = new THREE.Vector2(
        THREE.MathUtils.clamp(size.x * 0.72, 1.8, 4.8),
        THREE.MathUtils.clamp(size.y * 0.72, 1.8, 4.8),
      );
      return {
        area,
        light: {
          id: `room-light-${floor.id}`,
          position: new THREE.Vector3(
            center.x,
            center.y,
            Math.max(height - ROOM_LIGHT_HEIGHT_OFFSET, 1.8),
          ),
          intensity: THREE.MathUtils.clamp(maxSize * 0.24, 0.48, 1.15),
          fillIntensity: THREE.MathUtils.clamp(maxSize * 0.06, 0.12, 0.28),
          distance: Math.max(maxSize * 1.2, 4.2),
          diffuserSize,
        } satisfies RoomCeilingLightInfo,
      };
    })
    .filter(
      ({ light }) =>
        Number.isFinite(light.position.x) &&
        Number.isFinite(light.position.y) &&
        Number.isFinite(light.position.z),
    );

  if (candidates.length <= MAX_ROOM_CEILING_LIGHTS) {
    return candidates.map(({ light }) => light);
  }

  return candidates
    .sort((left, right) => right.area - left.area)
    .slice(0, MAX_ROOM_CEILING_LIGHTS)
    .map(({ light }) => light);
}

function getShapeBounds(shape: THREE.Shape | undefined): THREE.Box2 {
  const bounds = new THREE.Box2();
  shape?.getPoints(32).forEach((point) => bounds.expandByPoint(point));
  return bounds;
}

function getFallbackBoundsSize(bounds: THREE.Box2): THREE.Vector2 {
  return bounds.isEmpty()
    ? new THREE.Vector2(4, 4)
    : bounds.getSize(new THREE.Vector2());
}

function getShapeCenter(shape: THREE.Shape): THREE.Vector2 {
  const points = shape.getPoints(32);
  if (points.length < 3) {
    return getShapeBounds(shape).getCenter(new THREE.Vector2());
  }

  let areaFactor = 0;
  let x = 0;
  let y = 0;

  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    const cross = current.x * next.y - next.x * current.y;
    areaFactor += cross;
    x += (current.x + next.x) * cross;
    y += (current.y + next.y) * cross;
  }

  if (Math.abs(areaFactor) < 1e-8) {
    return getShapeBounds(shape).getCenter(new THREE.Vector2());
  }

  return new THREE.Vector2(x / (3 * areaFactor), y / (3 * areaFactor));
}
